import { DateTime } from 'luxon'
import type { ReminderKind } from './customers-types'
import {
  formatEuro,
  formatEuroCents,
  formatPercent,
} from '~/components/routes/store-dashboard/cards/format'

/**
 * Libellés des écrans Clients et Clients bannis (kanban #870). Pur et testé
 * (`customers-format.test.mjs`) : « jamais », « hier » ou « Banni · 18,60 € » sont des informations
 * de décision (relancer, débannir), pas de la décoration. Montants et pourcentages passent par les
 * formateurs communs des dashboards ; les dates par Luxon.
 */

/** « 486,20 € » ; `null` (donnée masquée ou inconnue) → « — ». */
export const formatEuros = (value: number | null | undefined): string =>
  value === null || value === undefined ? '—' : formatEuroCents(value)

/** « 161 642 € » : montants des tuiles, arrondis à l'euro. */
export const formatWholeEuros = (value: number | null | undefined): string =>
  value === null || value === undefined ? '—' : formatEuro(value)

/** Part entre 0 et 1 → « 3,8 % » ; `null` → « — ». */
export const formatShare = (ratio: number | null | undefined): string =>
  ratio === null || ratio === undefined ? '—' : formatPercent(ratio * 100)

const parseIso = (iso: string | null | undefined): DateTime | null => {
  if (iso === null || iso === undefined) return null
  const parsed = DateTime.fromISO(iso)
  return parsed.isValid ? parsed : null
}

/** Jours civils écoulés depuis `iso` ; `null` si absent ou invalide. */
export const daysSince = (
  iso: string | null | undefined,
  now: Date = new Date(),
): number | null => {
  const parsed = parseIso(iso)
  if (parsed === null) return null
  const today = DateTime.fromJSDate(now).startOf('day')
  // Arrondi : un changement d'heure fait un jour civil de 23 ou 25 h.
  return Math.round(today.diff(parsed.startOf('day'), 'days').days)
}

/**
 * Dernière entrée, dernier achat : « aujourd'hui, 08:12 », « hier, 12:41 », « il y a 3 j »,
 * « jamais ». `null` se lit **« jamais »** : sur une dernière entrée, l'absence est une information.
 */
export const formatRelativeDay = (
  iso: string | null | undefined,
  now: Date = new Date(),
): string => {
  const parsed = parseIso(iso)
  if (parsed === null) return 'jamais'
  const days = daysSince(iso, now) ?? 0
  if (days <= 0) return `aujourd'hui, ${parsed.toFormat('HH:mm')}`
  if (days === 1) return `hier, ${parsed.toFormat('HH:mm')}`
  return `il y a ${days} j`
}

/** « Banni depuis » : « 12 j », « aujourd'hui » ; inconnu → « — ». */
export const formatDaysSince = (iso: string | null | undefined, now: Date = new Date()): string => {
  const days = daysSince(iso, now)
  if (days === null) return '—'
  if (days <= 0) return "aujourd'hui"
  return `${days} j`
}

const REMINDER_KIND_LABELS: Record<ReminderKind, string> = {
  email: 'email',
  payment_link: 'lien',
}

/** Dernière relance : « email · il y a 10 j », « lien · hier », « jamais ». */
export const formatReminder = (
  iso: string | null | undefined,
  kind: ReminderKind | null | undefined,
  now: Date = new Date(),
): string => {
  const days = daysSince(iso, now)
  if (days === null) return 'jamais'
  const label = REMINDER_KIND_LABELS[kind ?? 'email']
  if (days <= 0) return `${label} · aujourd'hui`
  if (days === 1) return `${label} · hier`
  return `${label} · il y a ${days} j`
}

const CARD_BRAND_LABELS: Record<string, string> = {
  VISA: 'Visa',
  MASTERCARD: 'Mastercard',
  CB: 'CB',
  AMEX: 'Amex',
  MAESTRO: 'Maestro',
  SWILE: 'Swile',
}

/**
 * Colonne « Carte » : la marque quand le journal la connaît, « carte enregistrée » sinon, « aucune »
 * sans carte. Les derniers chiffres ne sont pas stockés : pas de « ••42 ».
 */
export const formatCard = (item: { hasCard?: boolean; cardBrand?: string | null }): string => {
  if (!item.hasCard) return 'aucune'
  if (!item.cardBrand) return 'carte enregistrée'
  const key = item.cardBrand.toUpperCase()
  return (
    CARD_BRAND_LABELS[key] ??
    item.cardBrand.charAt(0).toUpperCase() + item.cardBrand.slice(1).toLowerCase()
  )
}

export type StatusTone = 'alert' | 'favorable' | 'neutral'

export interface CustomerStatus {
  key: 'banned' | 'top' | 'new' | 'active'
  label: string
  tone: StatusTone
}

/**
 * Badges de la colonne « Statut », dans l'ordre d'importance : banni (avec la dette quand la
 * solvabilité est lue), gros acheteur, nouveau, actif. Un client sans aucun n'a pas de badge.
 */
export const customerStatuses = (item: {
  isBanned: boolean
  isTop: boolean
  isNew: boolean
  isActive: boolean
  unBanAmount?: number | null
}): CustomerStatus[] => {
  const statuses: CustomerStatus[] = []
  if (item.isBanned) {
    const amount =
      item.unBanAmount === null || item.unBanAmount === undefined
        ? ''
        : ` · ${formatEuros(item.unBanAmount)}`
    statuses.push({ key: 'banned', label: `Banni${amount}`, tone: 'alert' })
  }
  if (item.isTop) statuses.push({ key: 'top', label: 'Gros acheteur', tone: 'favorable' })
  if (item.isNew) statuses.push({ key: 'new', label: 'Nouveau', tone: 'neutral' })
  if (item.isActive) statuses.push({ key: 'active', label: 'Actif', tone: 'neutral' })
  return statuses
}

export const fullName = (item: { firstName: string; lastName: string }): string =>
  `${item.firstName} ${item.lastName}`.trim()

/** « Chloé B. » : auteur d'une action du journal ou d'un lien de paiement. */
export const adminShortName = (admin: { firstName: string; lastName: string }): string => {
  const initial = admin.lastName.trim().charAt(0).toUpperCase()
  return initial === '' ? admin.firstName : `${admin.firstName} ${initial}.`
}
